<script>
    import {Dropdown, Tooltip} from 'flowbite';

    import classnames from 'classnames';

    import {RideTypeToRouteType, RouteComputationTypes} from '@js/components/modules/constants';
    import * as Utils from '@js/modules/utils';
    import I18n from '@js/modules/translations';
    import StorageService from '@js/modules/storage';
    import AnalyticsService from '@js/modules/analyticsService';

    import {mapStore} from '@js/stores/mapStore';

    import {showProductModal} from '@js/actions/basketActions';

    import {clickOutside} from '@js/components/themes/actions/clickOutside';

    import Icon from '@js/components/themes/Icon.svelte';
    import {ROUTE_TYPE_ICON} from '@js/components/themes/icons';

    const {
        defaultRouteType = window.currentUserRideType ? RideTypeToRouteType[window.currentUserRideType] : 'hike',
        isSnapToRoadChecked = true,
        isFreehandDrawChecked = false,
        useRouteType = false,
        maxRouteTypeChange,
        isPoiModeChecked = false,
        onPoiModeChange,
        isWaypointModeChecked = false,
        onWaypointModeChange,
        onSnapToRoadChange,
        onFreehandDrawChange,
        onRouteTypeChange,
        onBackToHomeClick,
        onUndoClick,
        onRedoClick,
        isRedoAvailable = false,
        onReverseClick,
        onOutAndBackClick,
        onSectionColorToggle,
        isSectionColorActive = false,
        onClearClick,
        onExportGPXClick,
        hasElevationData = false,
        isElevationEnabled = false,
        onElevationToggle,
        isSlopePercentageEnabled = false,
        onSlopePercentageChange,
        isSurfaceTypeEnabled = false,
        onSurfaceTypeChange,
        isWaymarkedTrailsEnabled = false,
        onWaymarkedTrailsChange,
        isPreferPathsEnabled = false,
        onPreferPathsChange,
        onHighlightMapElement,
        mapElementHighlighted = [],
        alwaysVisible = false,
        // Fired when a tools menu opens, so the parent can prefetch deferred advanced modules ahead
        // of the user arming a feature. No-op by default.
        onMenuOpen = () => { /* no-op: parent may prefetch deferred modules */ },
        // Ride-show / read-only maps: show the display toggles (elevation, slope…) inline in the bar
        // instead of behind the "Properties" menu.
        inlineProperties = false
    } = $props();

    let routeTypeDropdown;
    let _previousRideType = defaultRouteType;

    let isSnapToRoad = $state(isSnapToRoadChecked);
    // Mirror the parent-owned freehand state so exiting via the mode banner / Esc unchecks the toggle.
    const isFreehandDraw = $derived(isFreehandDrawChecked);
    // Mirror the parent-owned POI/waypoint state so exiting a mode elsewhere (mode banner / Esc)
    // keeps the buttons in sync.
    const isPoiMode = $derived(isPoiModeChecked);
    const isWaypointMode = $derived(isWaypointModeChecked);
    let routeType = $state(defaultRouteType);
    let isEditMenuOpen = $state(false);
    let isPropsMenuOpen = $state(false);
    let isRouteTypeAvailable = $state(true);

    const hasRoute = $derived(!!$mapStore.currentDistance);

    const handleEditMenuOutsideClick = () => {
        if (!isEditMenuOpen) {
            return;
        }

        isEditMenuOpen = false;
    };

    const _handleToggleEditMenu = () => {
        // Openable with a route, or when only redo is left (after undoing to an empty route).
        if (!hasRoute && !isRedoAvailable) {
            return;
        }

        isPropsMenuOpen = false;
        isEditMenuOpen = !isEditMenuOpen;

        if (isEditMenuOpen) {
            onMenuOpen();
        }
    };

    const handlePropsMenuOutsideClick = () => {
        if (!isPropsMenuOpen) {
            return;
        }

        isPropsMenuOpen = false;
    };

    const _handleTogglePropsMenu = () => {
        isEditMenuOpen = false;
        isPropsMenuOpen = !isPropsMenuOpen;

        if (isPropsMenuOpen) {
            onMenuOpen();
        }
    };

    const _handleRedoClick = () => {
        isEditMenuOpen = false;

        onRedoClick();
    };

    // Independent on/off state per highlightable element (multi-select). The map toggle mirrors
    // this, so the menu can stay open to select several elements at once.
    const highlightSelection = $state({});

    // Placeholder text of the highlight select: shows the already-selected elements (CSS ellipsis
    // truncates when the list grows) so the current selection stays visible on the collapsed control.
    const highlightSummary = $derived.by(() => {
        const selected = mapElementHighlighted.filter((element) => highlightSelection[element]);

        if (!selected.length) {
            return I18n.t('js.maps.toolbar.highlight.select_placeholder');
        }

        return selected
            .map((element) => I18n.t(`js.maps.toolbar.highlight.elements.${element}`))
            .join(', ');
    });

    // When the highlightable set empties (e.g. switching off our vector tiles), the map drops the
    // highlight layers — clear the checkbox state so it can't show stale ticks on return.
    $effect(() => {
        if (mapElementHighlighted.length === 0 && Object.keys(highlightSelection).length > 0) {
            Object.keys(highlightSelection).forEach((element) => delete highlightSelection[element]);
        }
    });

    const _handleHighlightClick = (element) => {
        highlightSelection[element] = !highlightSelection[element];

        onHighlightMapElement(element);
    };

    // Native select of the highlightable elements: picking one toggles it, then resets to the
    // placeholder so several can be toggled in turn (the ✓ prefix shows what's active).
    const _handleHighlightSelect = (event) => {
        const element = event.target.value;
        if (element) {
            _handleHighlightClick(element);
        }

        event.target.value = '';
    };

    const _handleReverseClick = () => {
        isEditMenuOpen = false;

        onReverseClick();
    };

    const _handleOutAndBackClick = () => {
        isEditMenuOpen = false;

        onOutAndBackClick();
    };

    const _handleSectionColorToggle = () => {
        isEditMenuOpen = false;

        onSectionColorToggle();
    };

    const _onSnapToRoadChange = (event) => {
        isSnapToRoad = event.target.checked;

        onSnapToRoadChange(isSnapToRoad);
    };

    const _onFreehandDrawChange = (event) => {
        const checked = event.target.checked;

        // Track activations only (unique per visitor via _sendConversion).
        if (checked) {
            AnalyticsService.trackFreehandDrawClick();
        }

        onFreehandDrawChange(checked);
    };

    const _onPoiModeToggle = () => {
        isEditMenuOpen = false;

        onPoiModeChange(!isPoiMode);
    };

    const _onWaypointModeToggle = () => {
        isEditMenuOpen = false;

        onWaypointModeChange(!isWaypointMode);
    };

    const _onSlopePercentageChange = (event) => {
        if (onSlopePercentageChange) {
            AnalyticsService.trackSlopePercentageClick();

            onSlopePercentageChange(event.target.checked);

            // Close the menu so the recoloured track is visible (see the toggle's effect).
            isPropsMenuOpen = false;
        }
    };

    const _onSurfaceTypeChange = (event) => {
        if (onSurfaceTypeChange) {
            AnalyticsService.trackSurfaceTypeClick();

            onSurfaceTypeChange(event.target.checked);

            // Close the menu so the recoloured track is visible (see the toggle's effect).
            isPropsMenuOpen = false;
        }
    };

    const _onWaymarkedTrailsChange = (event) => {
        if (onWaymarkedTrailsChange) {
            onWaymarkedTrailsChange(event.target.checked);

            isPropsMenuOpen = false;
        }
    };

    const _onPreferPathsChange = (event) => {
        if (onPreferPathsChange) {
            onPreferPathsChange(event.target.checked);

            isPropsMenuOpen = false;
        }
    };

    const _onElevationToggle = (event) => {
        if (onElevationToggle) {
            onElevationToggle(event.target.checked);
        }
    };

    const _onRouteTypeChange = (newRouteType) => {
        routeType = newRouteType;
        routeTypeDropdown.hide();

        onRouteTypeChange(routeType);
    };

    const _handleBackHomeClick = () => {
        isEditMenuOpen = false;

        onBackToHomeClick();
    };

    const _handleClearClick = () => {
        isEditMenuOpen = false;

        onClearClick();
    };

    const _handleRouteTypeClick = (event) => {
        event.preventDefault();

        if (maxRouteTypeChange) {
            const mapMaxRouteTypeCount = StorageService.getItem('map_max_route_type_count') || 0;

            if (mapMaxRouteTypeCount >= maxRouteTypeChange) {
                isRouteTypeAvailable = false;
                showProductModal('map_max_route_type');
            }

            StorageService.setItem('map_max_route_type_count', mapMaxRouteTypeCount + 1);
        }
    };

    const routeTypeDropdownAction = (rideDropdownElement) => {
        $effect(() => {
            const rideDropdownTrigger = document.getElementById('ride-route-types');

            routeTypeDropdown = new Dropdown(rideDropdownElement, rideDropdownTrigger);

            if (!isRouteTypeAvailable) {
                const rideTooltipElement = document.getElementById('tooltip-route-type');
                const rideTooltipTrigger = document.getElementById('ride-route-types-tooltip');
                new Tooltip(rideTooltipElement, rideTooltipTrigger, {
                    placement: 'bottom',
                    triggerType: 'hover'
                });
            }
        });
    };

    const _handleExportGPXClick = () => {
        onExportGPXClick();
    };

    $effect(() => {
        if ($mapStore.currentRideType && _previousRideType !== $mapStore.currentRideType) {
            _previousRideType = $mapStore.currentRideType;

            routeType = RideTypeToRouteType[$mapStore.currentRideType];
        }
    });
</script>

{#snippet snapToRoadRender()}
    <div class="map-navigation-snap-to-road">
        <label class="flex flex-col items-center justify-center py-1 px-1 text-center text-primary-default cursor-pointer">
            <input type="checkbox"
                   class="sr-only peer snap-to-roads"
                   checked={isSnapToRoad}
                   onchange={_onSnapToRoadChange}
                   value="">
            <span class="relative w-8 h-3 mt-1 bg-gray-200 peer-focus:outline-hidden peer-focus:ring-2 peer-focus:ring-primary-500 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:h-4 peer-checked:after:w-4 peer-checked:after:start-[4px] peer-checked:after:top-[-2px] after:content-[''] after:absolute after:top-[0px] after:start-[0px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></span>

            <span class="button-label">
                {I18n.t('js.maps.toolbar.snap_to_road.title')}
            </span>
        </label>
    </div>
{/snippet}

{#snippet freehandDrawRender()}
    <div class="map-navigation-freehand-draw">
        <label class="flex flex-col items-center justify-center gap-1 py-1 px-2 text-center text-primary-default cursor-pointer">
            <input type="checkbox"
                   class="sr-only peer freehand-draw"
                   checked={isFreehandDraw}
                   onchange={_onFreehandDrawChange}
                   value="">
            <span class="relative w-8 h-3 bg-gray-200 peer-focus:outline-hidden peer-focus:ring-2 peer-focus:ring-primary-500 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:h-4 peer-checked:after:w-4 peer-checked:after:start-[4px] peer-checked:after:top-[-2px] after:content-[''] after:absolute after:top-[0px] after:start-[0px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></span>

            <span class="control-button-text">
                <span class="button-label">
                    {I18n.t('js.maps.toolbar.freehand_draw.title')}
                </span>
                <span class="control-button-desc">
                    {I18n.t('js.maps.toolbar.freehand_draw.desc')}
                </span>
            </span>
        </label>
    </div>
{/snippet}

{#snippet poiModeRender()}
    <div class="map-navigation-poi-mode">
        <button type="button"
                class={classnames('poi-mode-button', isPoiMode
                    ? 'bg-primary-600 text-white border-primary-700 hover:bg-primary-700'
                    : 'bg-white text-primary-default border-gray-300 hover:bg-gray-100 dark:bg-gray-700 dark:text-white dark:border-gray-600')}
                title={I18n.t('js.maps.toolbar.poi_mode.title')}
                aria-pressed={isPoiMode}
                onclick={_onPoiModeToggle}>
            <svg class="poi-mode-icon"
                 xmlns="http://www.w3.org/2000/svg"
                 viewBox="0 0 24 24"
                 fill="currentColor"
                 aria-hidden="true">
                <path d="M4,4H7L9,2H15L17,4H20A2,2 0 0,1 22,6V18A2,2 0 0,1 20,20H4A2,2 0 0,1 2,18V6A2,2 0 0,1 4,4M12,7A5,5 0 0,0 7,12A5,5 0 0,0 12,17A5,5 0 0,0 17,12A5,5 0 0,0 12,7M12,9A3,3 0 0,1 15,12A3,3 0 0,1 12,15A3,3 0 0,1 9,12A3,3 0 0,1 12,9Z"/>
            </svg>

            <span class="control-button-text">
                <span class="button-label">
                    {I18n.t('js.maps.toolbar.poi_mode.title')}
                </span>
                <span class="control-button-desc">
                    {I18n.t('js.maps.toolbar.poi_mode.desc')}
                </span>
            </span>
        </button>
    </div>
{/snippet}

{#snippet waypointModeRender()}
    <div class="map-navigation-poi-mode">
        <button type="button"
                class={classnames('poi-mode-button', isWaypointMode
                    ? 'bg-primary-600 text-white border-primary-700 hover:bg-primary-700'
                    : 'bg-white text-primary-default border-gray-300 hover:bg-gray-100 dark:bg-gray-700 dark:text-white dark:border-gray-600')}
                title={I18n.t('js.maps.toolbar.waypoint_mode.title')}
                aria-pressed={isWaypointMode}
                onclick={_onWaypointModeToggle}>
            <svg class="poi-mode-icon"
                 xmlns="http://www.w3.org/2000/svg"
                 viewBox="0 0 24 24"
                 fill="currentColor"
                 aria-hidden="true">
                <path d="M14.4,6L14,4H5V21H7V14H12.6L13,16H20V6H14.4Z"/>
            </svg>

            <span class="control-button-text">
                <span class="button-label">
                    {I18n.t('js.maps.toolbar.waypoint_mode.title')}
                </span>
                <span class="control-button-desc">
                    {I18n.t('js.maps.toolbar.waypoint_mode.desc')}
                </span>
            </span>
        </button>
    </div>
{/snippet}

{#snippet elevationRender()}
    <div class="map-navigation-elevation"
         class:opacity-50={!hasElevationData}>
        <label class="flex flex-col items-center justify-center py-1 px-1 text-center text-primary-default"
               class:cursor-pointer={hasElevationData}
               class:pointer-events-none={!hasElevationData}>
            <input type="checkbox"
                   class="sr-only peer"
                   checked={isElevationEnabled}
                   disabled={!hasElevationData}
                   onchange={_onElevationToggle}
                   value="">
            <span class="relative w-8 h-3 mt-1 bg-gray-200 peer-focus:outline-hidden peer-focus:ring-2 peer-focus:ring-primary-500 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:h-4 peer-checked:after:w-4 peer-checked:after:start-[4px] peer-checked:after:top-[-2px] after:content-[''] after:absolute after:top-[0px] after:start-[0px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></span>

            <span class="button-label">
                    {I18n.t('js.maps.toolbar.elevation.title')}
                </span>
        </label>
    </div>
{/snippet}

{#snippet slopePercentageRender()}
    <div class="map-navigation-slope-color"
         class:opacity-50={!hasElevationData}>
        <label class="flex flex-col items-center justify-center py-1 px-1 text-center text-primary-default"
               class:cursor-pointer={hasElevationData}
               class:pointer-events-none={!hasElevationData}>
            <input type="checkbox"
                   class="sr-only peer"
                   checked={isSlopePercentageEnabled}
                   disabled={!hasElevationData}
                   onchange={_onSlopePercentageChange}
                   value="">
            <span class="relative w-8 h-3 mt-1 bg-gray-200 peer-focus:outline-hidden peer-focus:ring-2 peer-focus:ring-primary-500 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:h-4 peer-checked:after:w-4 peer-checked:after:start-[4px] peer-checked:after:top-[-2px] after:content-[''] after:absolute after:top-[0px] after:start-[0px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></span>

            <span class="control-button-text">
                <span class="button-label">
                    {I18n.t('js.maps.toolbar.slope_color.title')}
                </span>
                {#if !inlineProperties}
                    <span class="control-button-desc">
                        {I18n.t('js.maps.toolbar.slope_color.desc')}
                    </span>
                {/if}
            </span>
        </label>
    </div>
{/snippet}

{#snippet surfaceTypeRender()}
    <div class="map-navigation-surface-color">
        <label class="flex flex-col items-center justify-center py-1 px-1 text-center text-primary-default cursor-pointer">
            <input type="checkbox"
                   class="sr-only peer"
                   checked={isSurfaceTypeEnabled}
                   onchange={_onSurfaceTypeChange}
                   value="">
            <span class="relative w-8 h-3 mt-1 bg-gray-200 peer-focus:outline-hidden peer-focus:ring-2 peer-focus:ring-primary-500 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:h-4 peer-checked:after:w-4 peer-checked:after:start-[4px] peer-checked:after:top-[-2px] after:content-[''] after:absolute after:top-[0px] after:start-[0px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></span>

            <span class="control-button-text">
                <span class="button-label">
                    {I18n.t('js.maps.toolbar.surface_color.title')}
                </span>
                <span class="control-button-desc">
                    {I18n.t('js.maps.toolbar.surface_color.desc')}
                </span>
            </span>
        </label>
    </div>
{/snippet}

{#snippet waymarkedTrailsRender()}
    <div class="map-navigation-waymarked-trails">
        <label class="flex flex-col items-center justify-center py-1 px-1 text-center text-primary-default cursor-pointer">
            <input type="checkbox"
                   class="sr-only peer"
                   checked={isWaymarkedTrailsEnabled}
                   onchange={_onWaymarkedTrailsChange}
                   value="">
            <span class="relative w-8 h-3 mt-1 bg-gray-200 peer-focus:outline-hidden peer-focus:ring-2 peer-focus:ring-primary-500 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:h-4 peer-checked:after:w-4 peer-checked:after:start-[4px] peer-checked:after:top-[-2px] after:content-[''] after:absolute after:top-[0px] after:start-[0px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></span>

            <span class="control-button-text">
                <span class="button-label">
                    {I18n.t('js.maps.toolbar.waymarked_trails.title')}
                </span>
                <span class="control-button-desc">
                    {I18n.t('js.maps.toolbar.waymarked_trails.desc')}
                </span>
            </span>
        </label>
    </div>
{/snippet}

{#snippet preferPathsRender()}
    <div class="map-navigation-prefer-paths">
        <label class="flex flex-col items-center justify-center py-1 px-1 text-center text-primary-default cursor-pointer">
            <input type="checkbox"
                   class="sr-only peer"
                   checked={isPreferPathsEnabled}
                   onchange={_onPreferPathsChange}
                   value="">
            <span class="relative w-8 h-3 mt-1 bg-gray-200 peer-focus:outline-hidden peer-focus:ring-2 peer-focus:ring-primary-500 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:h-4 peer-checked:after:w-4 peer-checked:after:start-[4px] peer-checked:after:top-[-2px] after:content-[''] after:absolute after:top-[0px] after:start-[0px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></span>

            <span class="control-button-text">
                <span class="button-label">
                    {I18n.t('js.maps.toolbar.prefer_paths.title')}
                </span>
                <span class="control-button-desc">
                    {I18n.t('js.maps.toolbar.prefer_paths.desc')}
                </span>
            </span>
        </label>
    </div>
{/snippet}

{#snippet undoRender()}
    <div class="map-navigation-control">
        <button class="control-button undo-control"
                type="button"
                title={I18n.t('js.maps.toolbar.undo')}
                onclick={onUndoClick}>
            <svg class="control-button-icon inline-flex w-6 h-6 my-1"
                 xmlns="http://www.w3.org/2000/svg"
                 viewBox="0 0 24 24"
                 fill="currentColor">
                <path d="M12.5,8C9.85,8 7.45,9 5.6,10.6L2,7V16H11L7.38,12.38C8.77,11.22 10.54,10.5 12.5,10.5C16.04,10.5 19.05,12.81 20.1,16L22.47,15.22C21.08,11.03 17.15,8 12.5,8Z"/>
            </svg>
            <span class="button-label">
                {I18n.t('js.maps.toolbar.undo')}
            </span>
        </button>
    </div>
{/snippet}

{#snippet backHomeRender()}
    <div class="map-navigation-back-to-home">
        <button class="control-button back-to-home"
                type="button"
                title={I18n.t('js.maps.toolbar.back_to_home')}
                onclick={_handleBackHomeClick}>
            <svg class="control-button-icon inline-flex w-6 h-6 my-1"
                 xmlns="http://www.w3.org/2000/svg"
                 viewBox="0 0 24 24"
                 fill="currentColor">
                <path d="M20,13.5A6.5,6.5 0 0,1 13.5,20H6V18H13.5A4.5,4.5 0 0,0 18,13.5A4.5,4.5 0 0,0 13.5,9H7.83L10.91,12.09L9.5,13.5L4,8L9.5,2.5L10.91,3.91L7.83,7H13.5A6.5,6.5 0 0,1 20,13.5Z"/>
            </svg>
            <span class="control-button-text">
                <span class="button-label">
                    {I18n.t('js.maps.toolbar.back_to_home')}
                </span>
                <span class="control-button-desc">
                    {I18n.t('js.maps.toolbar.back_to_home_desc')}
                </span>
            </span>
        </button>
    </div>
{/snippet}

{#snippet reverseRender()}
    <div class="map-navigation-control">
        <button class="control-button"
                type="button"
                title={I18n.t('js.maps.toolbar.edit.reverse')}
                onclick={_handleReverseClick}>
            <svg class="control-button-icon inline-flex w-6 h-6 my-1"
                 xmlns="http://www.w3.org/2000/svg"
                 viewBox="0 0 24 24"
                 fill="currentColor">
                <path d="M21,9L17,5V8H10V10H17V13M7,11L3,15L7,19V16H14V14H7V11Z"/>
            </svg>
            <span class="control-button-text">
                <span class="button-label">
                    {I18n.t('js.maps.toolbar.edit.reverse')}
                </span>
                <span class="control-button-desc">
                    {I18n.t('js.maps.toolbar.edit.reverse_desc')}
                </span>
            </span>
        </button>
    </div>
{/snippet}

{#snippet outAndBackRender()}
    <div class="map-navigation-control">
        <button class="control-button"
                type="button"
                title={I18n.t('js.maps.toolbar.edit.out_and_back')}
                onclick={_handleOutAndBackClick}>
            <svg class="control-button-icon inline-flex w-6 h-6 my-1"
                 xmlns="http://www.w3.org/2000/svg"
                 viewBox="0 0 24 24"
                 fill="currentColor">
                <path d="M6.45,17.45L1,12L6.45,6.55L7.86,7.96L4.83,11H19.17L16.14,7.96L17.55,6.55L23,12L17.55,17.45L16.14,16.04L19.17,13H4.83L7.86,16.04L6.45,17.45Z"/>
            </svg>
            <span class="control-button-text">
                <span class="button-label">
                    {I18n.t('js.maps.toolbar.edit.out_and_back')}
                </span>
                <span class="control-button-desc">
                    {I18n.t('js.maps.toolbar.edit.out_and_back_desc')}
                </span>
            </span>
        </button>
    </div>
{/snippet}

{#snippet sectionColorRender()}
    <div class="map-navigation-control">
        <button class="control-button"
                class:active={isSectionColorActive}
                type="button"
                title={I18n.t('js.maps.toolbar.sections.title')}
                aria-pressed={isSectionColorActive}
                onclick={_handleSectionColorToggle}>
            <svg class="control-button-icon inline-flex w-6 h-6 my-1"
                 xmlns="http://www.w3.org/2000/svg"
                 viewBox="0 0 24 24"
                 fill="currentColor">
                <path d="M12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2C17.5,2 22,6 22,11A5,5 0 0,1 17,16H15.2C14.9,16 14.7,16.2 14.7,16.5C14.7,16.6 14.8,16.7 14.8,16.8C15.2,17.3 15.4,17.9 15.4,18.5A2.5,2.5 0 0,1 13,21H12M12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20H13A0.5,0.5 0 0,0 13.5,19.5C13.5,19.4 13.4,19.3 13.4,19.2C13,18.7 12.8,18.1 12.8,17.5A2.5,2.5 0 0,1 15.3,15H17A3,3 0 0,0 20,12C20,7.6 16.4,4 12,4M6.5,10A1.5,1.5 0 0,1 8,11.5A1.5,1.5 0 0,1 6.5,13A1.5,1.5 0 0,1 5,11.5A1.5,1.5 0 0,1 6.5,10M9.5,6A1.5,1.5 0 0,1 11,7.5A1.5,1.5 0 0,1 9.5,9A1.5,1.5 0 0,1 8,7.5A1.5,1.5 0 0,1 9.5,6M14.5,6A1.5,1.5 0 0,1 16,7.5A1.5,1.5 0 0,1 14.5,9A1.5,1.5 0 0,1 13,7.5A1.5,1.5 0 0,1 14.5,6M17.5,10A1.5,1.5 0 0,1 19,11.5A1.5,1.5 0 0,1 17.5,13A1.5,1.5 0 0,1 16,11.5A1.5,1.5 0 0,1 17.5,10Z"/>
            </svg>
            <span class="control-button-text">
                <span class="button-label">
                    {I18n.t('js.maps.toolbar.sections.title')}
                </span>
                <span class="control-button-desc">
                    {I18n.t('js.maps.toolbar.sections.hint')}
                </span>
            </span>
        </button>
    </div>
{/snippet}

{#snippet redoRender()}
    <div class="map-navigation-control">
        <button class="control-button redo-control"
                class:disabled={!isRedoAvailable}
                type="button"
                title={I18n.t('js.maps.toolbar.redo')}
                disabled={!isRedoAvailable}
                onclick={_handleRedoClick}>
            <svg class="control-button-icon inline-flex w-6 h-6 my-1"
                 xmlns="http://www.w3.org/2000/svg"
                 viewBox="0 0 24 24"
                 fill="currentColor">
                <path d="M18.4,10.6C16.55,9 14.15,8 11.5,8C6.85,8 2.92,11.03 1.54,15.22L3.9,16C4.95,12.81 7.95,10.5 11.5,10.5C13.45,10.5 15.23,11.22 16.62,12.38L13,16H22V7L18.4,10.6Z"/>
            </svg>
            <span class="control-button-text">
                <span class="button-label">
                    {I18n.t('js.maps.toolbar.redo')}
                </span>
            </span>
        </button>
    </div>
{/snippet}

{#snippet highlightRender()}
    <div class="map-navigation-highlight">
        <span class="map-menu-section-label">
            {I18n.t('js.maps.toolbar.highlight.title')}
        </span>

        <select class="map-highlight-select"
                aria-label={I18n.t('js.maps.toolbar.highlight.title')}
                onchange={_handleHighlightSelect}>
            <option value="">
                {highlightSummary}
            </option>
            {#each mapElementHighlighted as element (element)}
                <option value={element}>
                    {highlightSelection[element] ? '✓ ' : ''}{I18n.t(`js.maps.toolbar.highlight.elements.${element}`)}
                </option>
            {/each}
        </select>
    </div>
{/snippet}

{#snippet editMenuRender()}
    <div class="map-navigation-control map-edit-menu">
        <button id="map-edit-menu-button"
                class="control-button map-menu-toggle"
                class:disabled={!hasRoute && !isRedoAvailable}
                type="button"
                title={I18n.t('js.maps.toolbar.menu.edit')}
                aria-haspopup="true"
                aria-expanded={isEditMenuOpen}
                disabled={!hasRoute && !isRedoAvailable}
                onclick={_handleToggleEditMenu}>
            <svg class="control-button-icon inline-flex w-6 h-6 my-1"
                 xmlns="http://www.w3.org/2000/svg"
                 viewBox="0 0 24 24"
                 fill="currentColor">
                <path d="M20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18,2.9 17.35,2.9 16.96,3.29L15.12,5.12L18.87,8.87M3,17.25V21H6.75L17.81,9.93L14.06,6.18L3,17.25Z"/>
            </svg>
            <span class="map-menu-toggle-text">
                <span class="button-label">
                    {I18n.t('js.maps.toolbar.menu.edit')}
                </span>
                <svg class="map-menu-caret"
                     xmlns="http://www.w3.org/2000/svg"
                     viewBox="0 0 10 6"
                     fill="none"
                     aria-hidden="true">
                    <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="m1 1 4 4 4-4"/>
                </svg>
            </span>
        </button>

        {#if isEditMenuOpen}
            <div class="map-edit-menu-popover"
                 {@attach clickOutside(handleEditMenuOutsideClick, 'map-edit-menu-button')}>
                {#if onRedoClick}
                    {@render redoRender()}
                {/if}

                {#if onReverseClick}
                    {@render reverseRender()}
                {/if}

                {#if onBackToHomeClick}
                    {@render backHomeRender()}
                {/if}

                {#if onOutAndBackClick}
                    {@render outAndBackRender()}
                {/if}

                {#if onSectionColorToggle}
                    {@render sectionColorRender()}
                {/if}

                {#if onClearClick}
                    {@render clearRender()}
                {/if}

                {#if onWaypointModeChange}
                    {@render waypointModeRender()}
                {/if}

                {#if onPoiModeChange}
                    {@render poiModeRender()}
                {/if}

                {#if onExportGPXClick}
                    {@render exportGPXRender()}
                {/if}
            </div>
        {/if}
    </div>
{/snippet}

{#snippet propsMenuRender()}
    <div class="map-navigation-control map-edit-menu">
        <button id="map-props-menu-button"
                class="control-button map-menu-toggle"
                type="button"
                title={I18n.t('js.maps.toolbar.menu.props')}
                aria-haspopup="true"
                aria-expanded={isPropsMenuOpen}
                onclick={_handleTogglePropsMenu}>
            <svg class="control-button-icon inline-flex w-6 h-6 my-1"
                 xmlns="http://www.w3.org/2000/svg"
                 viewBox="0 0 24 24"
                 fill="currentColor">
                <path d="M12,15.5A3.5,3.5 0 0,1 8.5,12A3.5,3.5 0 0,1 12,8.5A3.5,3.5 0 0,1 15.5,12A3.5,3.5 0 0,1 12,15.5M19.43,12.97C19.47,12.65 19.5,12.33 19.5,12C19.5,11.67 19.47,11.34 19.43,11L21.54,9.37C21.73,9.22 21.78,8.95 21.66,8.73L19.66,5.27C19.54,5.05 19.27,4.96 19.05,5.05L16.56,6.05C16.04,5.66 15.5,5.32 14.87,5.07L14.5,2.42C14.46,2.18 14.25,2 14,2H10C9.75,2 9.54,2.18 9.5,2.42L9.13,5.07C8.5,5.32 7.96,5.66 7.44,6.05L4.95,5.05C4.73,4.96 4.46,5.05 4.34,5.27L2.34,8.73C2.21,8.95 2.27,9.22 2.46,9.37L4.57,11C4.53,11.34 4.5,11.67 4.5,12C4.5,12.33 4.53,12.65 4.57,12.97L2.46,14.63C2.27,14.78 2.21,15.05 2.34,15.27L4.34,18.73C4.46,18.95 4.73,19.03 4.95,18.95L7.44,17.94C7.96,18.34 8.5,18.68 9.13,18.93L9.5,21.58C9.54,21.82 9.75,22 10,22H14C14.25,22 14.46,21.82 14.5,21.58L14.87,18.93C15.5,18.67 16.04,18.34 16.56,17.94L19.05,18.95C19.27,19.03 19.54,18.95 19.66,18.73L21.66,15.27C21.78,15.05 21.73,14.78 21.54,14.63L19.43,12.97Z"/>
            </svg>
            <span class="map-menu-toggle-text">
                <span class="button-label">
                    {I18n.t('js.maps.toolbar.menu.props')}
                </span>
                <svg class="map-menu-caret"
                     xmlns="http://www.w3.org/2000/svg"
                     viewBox="0 0 10 6"
                     fill="none"
                     aria-hidden="true">
                    <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="m1 1 4 4 4-4"/>
                </svg>
            </span>
        </button>

        {#if isPropsMenuOpen}
            <div class="map-edit-menu-popover"
                 {@attach clickOutside(handlePropsMenuOutsideClick, 'map-props-menu-button')}>
                {#if onSnapToRoadChange}
                    {@render snapToRoadRender()}
                {/if}

                {#if onFreehandDrawChange}
                    {@render freehandDrawRender()}
                {/if}

                {#if useRouteType}
                    {@render routeTypeBlockRender()}
                {/if}

                {#if onElevationToggle}
                    {#if onSnapToRoadChange || onFreehandDrawChange || useRouteType}
                        <div class="map-menu-separator" role="separator" aria-orientation="horizontal"></div>
                    {/if}
                    {@render elevationRender()}
                {/if}

                {#if onSlopePercentageChange}
                    {@render slopePercentageRender()}
                {/if}

                {#if onSurfaceTypeChange}
                    {@render surfaceTypeRender()}
                {/if}

                {#if onWaymarkedTrailsChange}
                    {@render waymarkedTrailsRender()}
                {/if}

                {#if onPreferPathsChange}
                    {@render preferPathsRender()}
                {/if}

                {#if onHighlightMapElement && mapElementHighlighted.length}
                    {@render highlightRender()}
                {/if}
            </div>
        {/if}
    </div>
{/snippet}

{#snippet clearRender()}
    <div class="map-navigation-control">
        <button class="control-button clear-control"
                type="button"
                title={I18n.t('js.maps.toolbar.clear')}
                onclick={_handleClearClick}>
            <svg class="control-button-icon inline-flex w-6 h-6 my-1"
                 xmlns="http://www.w3.org/2000/svg"
                 viewBox="0 0 24 24"
                 fill="currentColor">
                <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z"/>
            </svg>
            <span class="button-label">
                {I18n.t('js.maps.toolbar.clear')}
            </span>
        </button>
    </div>
{/snippet}

{#snippet exportGPXRender()}
    <div class="map-navigation-control">
        <button class="control-button clear-control"
                type="button"
                title={I18n.t('js.maps.toolbar.export_gpx')}
                onclick={_handleExportGPXClick}>
            <svg class="control-button-icon inline-flex w-4 h-4 my-1"
                 xmlns="http://www.w3.org/2000/svg"
                 viewBox="0 0 24 24"
                 fill="currentColor">
                <path d="M5,20H19V18H5M19,9H15V3H9V9H5L12,16L19,9Z"/>
            </svg>
            <span class="button-label">
                {I18n.t('js.maps.toolbar.export_gpx')}
            </span>
        </button>
    </div>
{/snippet}

{#snippet routeTypeBlockRender()}
    <div id="route-type-element"
         class="map-navigation-route-type">
        <div id="ride-route-types-tooltip"
             class="w-fit"
             data-tooltip-target={isRouteTypeAvailable ? undefined : 'tooltip-route-type'}
             data-tooltip-placement="bottom">
            <div class="flex">
                <button type="button"
                        id="ride-route-types"
                        class="button-label shrink-0 inline-flex items-center py-1.5 px-2 font-medium text-center text-primary-default bg-white border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-200 focus:ring-4 focus:outline-hidden focus:ring-gray-100 dark:bg-gray-700 dark:hover:bg-gray-600 dark:focus:ring-gray-700 dark:text-white dark:border-gray-600"
                        data-dropdown-toggle="route-type-dropdown"
                        onclick={_handleRouteTypeClick}>
                    <Icon class="h-4 w-4 me-2"
                          icon={ROUTE_TYPE_ICON[routeType]}/>
                    {I18n.t(`js.maps.toolbar.route_type.types.${routeType}`)}
                    <svg class="w-2.5 h-2.5 ms-2.5"
                         aria-hidden="true"
                         xmlns="http://www.w3.org/2000/svg"
                         fill="none"
                         viewBox="0 0 10 6">
                        <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"
                              stroke-width="2"
                              d="m1 1 4 4 4-4"/>
                    </svg>
                </button>

                <div use:routeTypeDropdownAction
                     id="route-type-dropdown"
                     class="hidden w-36 bg-white divide-y divide-gray-100 rounded-lg shadow-sm z-dropdown dark:bg-gray-700">
                    <ul class="py-1 text-sm text-gray-700 dark:text-gray-200"
                        aria-labelledby="ride-route-types">
                        {#each Object.entries(Utils.sortObjectByKeys(I18n.t('js.maps.toolbar.route_type.types'), RouteComputationTypes)) as [type, routeName] (type)}
                            <li>
                                <button type="button"
                                        class="inline-flex w-full px-2 py-1 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-600 dark:hover:text-white"
                                        class:bg-primary-200={type === routeType}
                                        onclick={() => _onRouteTypeChange(type)}>
                                    <span class="inline-flex items-center whitespace-nowrap">
                                        <Icon class="h-4 w-4 me-3"
                                              icon={ROUTE_TYPE_ICON[type]}/>
                                        {routeName}
                                    </span>
                                </button>
                            </li>
                        {/each}
                    </ul>
                </div>
            </div>
        </div>

        {#if !isRouteTypeAvailable}
            <div id="tooltip-route-type"
                 role="tooltip"
                 class="absolute invisible inline-block px-3 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg shadow-xs opacity-0 tooltip z-tooltip dark:bg-gray-700">
                {I18n.t('js.maps.toolbar.route_type.tooltip')}
                <div class="tooltip-arrow"
                     data-popper-arrow></div>
            </div>
        {/if}
    </div>
{/snippet}

{#if onUndoClick || onRedoClick || onBackToHomeClick || onReverseClick || onOutAndBackClick || onSectionColorToggle || onPoiModeChange || onWaypointModeChange || onClearClick || onSnapToRoadChange || onFreehandDrawChange || useRouteType || onElevationToggle || onSlopePercentageChange || onSurfaceTypeChange || onWaymarkedTrailsChange || onPreferPathsChange || onExportGPXClick || (onHighlightMapElement && mapElementHighlighted.length)}
    <div class={classnames('map-navigation-control-container', {'map-navigation-container-always-visible': alwaysVisible})}>
        {#if onUndoClick}
            {@render undoRender()}
        {/if}

        {#if inlineProperties}
            {#if onSnapToRoadChange}
                {@render snapToRoadRender()}
            {/if}
            {#if onFreehandDrawChange}
                {@render freehandDrawRender()}
            {/if}
            {#if useRouteType}
                {@render routeTypeBlockRender()}
            {/if}
            {#if onElevationToggle}
                {@render elevationRender()}
            {/if}
            {#if onSlopePercentageChange}
                {@render slopePercentageRender()}
            {/if}
            {#if onSurfaceTypeChange}
                {@render surfaceTypeRender()}
            {/if}
            {#if onWaymarkedTrailsChange}
                {@render waymarkedTrailsRender()}
            {/if}
            {#if onPreferPathsChange}
                {@render preferPathsRender()}
            {/if}
            {#if onHighlightMapElement && mapElementHighlighted.length}
                {@render highlightRender()}
            {/if}
        {:else if onSnapToRoadChange || onFreehandDrawChange || useRouteType || onElevationToggle || onSlopePercentageChange || onSurfaceTypeChange || onWaymarkedTrailsChange || onPreferPathsChange || (onHighlightMapElement && mapElementHighlighted.length)}
            {@render propsMenuRender()}
        {/if}

        {#if onRedoClick || onReverseClick || onOutAndBackClick || onSectionColorToggle || onPoiModeChange || onWaypointModeChange || onBackToHomeClick || onClearClick || onExportGPXClick}
            {@render editMenuRender()}
        {/if}
    </div>
{/if}
